import {useEffect, useRef, useState} from 'react'

import {focusCell, focusOmnibar, type TableFocus} from '../components/table/navigation'
import type {MemexItemModel} from '../models/memex-item-model'
import type {SetFocusStateAction} from '../navigation/context'
import {isDragDropColumnId} from './tanstack-table/helpers/column-ids'
import type {Table} from './tanstack-table/helpers/types'
import {useViews} from './use-views'

type SetTableFocusAction = SetFocusStateAction<TableFocus>

function getFocusFirstCellAction(table: Table<MemexItemModel>) {
  const flatRows = table.getRowModel().flatRows
  const firstRow = flatRows.find(row => !row.getIsGrouped())
  const visibleColumns = table.getVisibleLeafColumns()

  const firstVisibleColumn = visibleColumns.find(column => !isDragDropColumnId(column.id))

  if (firstVisibleColumn && firstRow) {
    return focusCell(firstRow.id, firstVisibleColumn.id)
  }

  return null
}

export function useReinitializeFocusStateEffect({
  currentViewNumber,
  setInitialFocalPoint,
  table,
}: {
  currentViewNumber?: number
  setInitialFocalPoint: (action: SetTableFocusAction | null) => void
  table: Table<MemexItemModel>
}) {
  const previousCurrentViewNumber = useRef(currentViewNumber)

  /**
   * This effect should only run
   * - When the view number changes
   * - when the memex is not new
   */
  useEffect(() => {
    const isViewNumberUnchanged = previousCurrentViewNumber.current === currentViewNumber

    // When we have a view that's unchanged, skip running this
    if (isViewNumberUnchanged) return

    setInitialFocalPoint(getFocusFirstCellAction(table))
  }, [currentViewNumber, setInitialFocalPoint, table])

  useEffect(() => {
    previousCurrentViewNumber.current = currentViewNumber
  })
}

/**
 * A hook that tracks the initial focusable cell
 * for a given table, in state - re-evaluating
 * whenever the currentView.number changes
 */
export const useInitialTableFocusAction = (table: Table<MemexItemModel>) => {
  const {currentView} = useViews()

  const [initialFocalPoint, setInitialFocalPoint] = useState<SetTableFocusAction | null>(() => {
    return getFocusFirstCellAction(table) ?? focusOmnibar()
  })

  useReinitializeFocusStateEffect({
    currentViewNumber: currentView?.number,
    setInitialFocalPoint,
    table,
  })

  return initialFocalPoint
}
